import { useState } from "react";
import { ImageOff } from "lucide-react";
import { PHOTO_UPLOADS_ENABLED } from "@shared/constants/features";
import { useClearChildPhoto } from "../../lib/api/useChildren";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "../ui/ConfirmDialog";

/**
 * Shown only to a parent who saved a child photo BEFORE photos were switched off
 * (BRIEF §5.3). The server no longer returns the image, only `hasPhoto`, so
 * without this the parent would have no way to delete it short of deleting the
 * whole profile. Removing is always allowed (DELETE /api/children/:id/photo).
 *
 * Renders nothing while photo uploads are on (the uploader has its own trash
 * button then) or when no photo is stored.
 */
export function StoredPhotoNotice({ childId, hasPhoto }: { childId: string; hasPhoto: boolean }) {
  const clearPhoto = useClearChildPhoto(childId);
  const { publish } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (PHOTO_UPLOADS_ENABLED || !hasPhoto) return null;

  const remove = async () => {
    try {
      await clearPhoto.mutateAsync();
      setConfirmOpen(false);
      publish({ variant: "success", title: "Photo removed" });
    } catch (err) {
      setConfirmOpen(false);
      publish({
        variant: "error",
        title: "Couldn't remove the photo",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-amber-50/90 p-4 ring-1 ring-amber-200 sm:flex-row sm:items-center">
      <ImageOff className="h-5 w-5 shrink-0 text-amber-700" aria-hidden />
      <p className="flex-1 text-sm text-neutral-800">
        A photo you added earlier is still saved. To keep children safe we no longer show
        children&apos;s photos anywhere, including on care links. You can delete it for good.
      </p>
      <Button
        variant="destructiveOutline"
        size="sm"
        className="self-start sm:self-auto"
        onClick={() => setConfirmOpen(true)}
      >
        Delete photo
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete the saved photo?"
        description="This removes the photo from Leena's List for good. It can't be undone."
        confirmLabel="Delete photo"
        destructive
        isPending={clearPhoto.isPending}
        onConfirm={() => {
          void remove();
        }}
      />
    </div>
  );
}
